import { apiRequest } from '../utils/apiClient';
import { roleLabel, type ActivityType } from '../components/registration/intent/roleCatalog';

// Bucket C — the public professional directory ("ابحث عن"). Wraps the
// flag-gated /api/professionals endpoints (the apiClient base already includes
// /api). Read-only/public, so no token is sent.

export type DirectoryActivityType = 'BROKER' | 'PROPERTY_MANAGEMENT' | 'FACILITIES_MANAGEMENT' | 'APPRAISER' | 'CONSULTANT';
export type OwnerType = 'individual' | 'company';

export interface ProfessionalListItem {
  id: string; // activityId
  activityType: DirectoryActivityType;
  ownerType: OwnerType;
  displayName: string | null;
  bio: string | null;
  avatarUrl: string | null;
  city: string | null;
  region: string | null;
  businessPhone: string | null;
  whatsapp: string | null;
  avgRating: number;
  totalJobs: number;
  totalReviews: number;
  companySlug: string | null;
}

export interface ProfessionalsListResponse {
  data: ProfessionalListItem[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
}

export interface ProfessionalBadge {
  activityType: ActivityType;
  status: string;
  verifiedAt: string | null;
  validUntil: string | null;
}

export interface ProfessionalProfileData {
  activity: { id: string; activityType: DirectoryActivityType; status: string; validUntil: string | null };
  ownerType: OwnerType;
  profile: {
    displayName: string | null;
    bio: string | null;
    avatarUrl: string | null;
    businessPhone: string | null;
    businessEmail: string | null;
    whatsapp: string | null;
    website: string | null;
    yearsExperience: number | null;
    specialties: string[];
    serviceAreas: string[];
    city: string | null;
    region: string | null;
    avgRating: number;
    totalJobs: number;
    totalReviews: number;
  } | null;
  owner: {
    userId: string | null;
    companyId: string | null;
    agentId: string | null;
    name: string | null;
    avatarUrl: string | null;
    companyName: string | null;
    companyNameAr: string | null;
    companySlug: string | null;
  };
  badges: ProfessionalBadge[];
}

export interface DirectoryTypeDef {
  type: DirectoryActivityType;
  en: string;
  ar: string;
  singularEn: string;
  singularAr: string;
  icon: string;
}

// The public directory types + their plural (tab/section) and singular
// (card/role) labels. The plural labels read as a category; the singular ones
// label one professional. Icons mirror roleCatalog for consistency with
// "Register with us". Consultants are intentionally hidden from the public
// directory for now (owner request); the CONSULTANT type stays in the union so
// existing data still resolves, it's just not offered as a tab.
export const DIRECTORY_TYPES: DirectoryTypeDef[] = [
  { type: 'BROKER', en: 'Brokers & marketers', ar: 'المسوقون والوسطاء العقاريون', singularEn: 'Broker & marketer', singularAr: 'مسوّق ووسيط عقاري', icon: 'ri-key-2-line' },
  { type: 'PROPERTY_MANAGEMENT', en: 'Property management', ar: 'إدارة الأملاك', singularEn: 'Property management', singularAr: 'إدارة أملاك', icon: 'ri-building-2-line' },
  { type: 'FACILITIES_MANAGEMENT', en: 'Facilities management', ar: 'إدارة المرافق', singularEn: 'Facilities management', singularAr: 'إدارة مرافق', icon: 'ri-tools-line' },
  { type: 'APPRAISER', en: 'Appraisers', ar: 'المقيّمون العقاريون', singularEn: 'Appraiser', singularAr: 'مقيّم عقاري', icon: 'ri-scales-3-line' },
];

export const DIRECTORY_TYPE_VALUES: DirectoryActivityType[] = DIRECTORY_TYPES.map((d) => d.type);

export function directoryTypeDef(type: string): DirectoryTypeDef | undefined {
  return DIRECTORY_TYPES.find((d) => d.type === type);
}

// Label for ANY activity type shown as a badge (incl. developer/investor/analyst
// an owner may also hold) — reuses the single-source roleCatalog labels.
export function badgeLabel(type: ActivityType, ownerType: OwnerType, isAr: boolean): string {
  return roleLabel(type, ownerType === 'company' ? 'company' : 'individual', isAr);
}

export async function fetchProfessionals(params: {
  activityType: DirectoryActivityType;
  city?: string;
  ownerType?: OwnerType;
  page?: number;
  pageSize?: number;
}): Promise<ProfessionalsListResponse> {
  const q = new URLSearchParams();
  q.set('activityType', params.activityType);
  if (params.city) q.set('city', params.city);
  if (params.ownerType) q.set('ownerType', params.ownerType);
  q.set('page', String(params.page ?? 1));
  q.set('pageSize', String(params.pageSize ?? 20));
  return apiRequest<ProfessionalsListResponse>(`/professionals?${q.toString()}`);
}

export async function fetchProfessionalProfile(activityId: string): Promise<ProfessionalProfileData> {
  const res = await apiRequest<{ data: ProfessionalProfileData }>(`/professionals/${activityId}`);
  return res.data;
}
